import React, { useEffect, useState } from "react";
import {
  Badge,
  Button,
  Col,
  Container,
  Form,
  Modal,
  Row,
  Table,
} from "react-bootstrap";
import { apiWrapper } from "apis/CommonApi";
import * as api from "apis/SolaronPolicyApi";
import "./UsersPage.scss";

const codePattern = /^[0-9A-Fa-f]{2}-[0-9A-Fa-f]{2}$/;

const parseCode = (value) =>
  codePattern.test(value) ? parseInt(value.replace("-", ""), 16) : null;

const formatCode = (value) => {
  if (!Number.isInteger(value) || value < 0 || value > 65535) {
    return "";
  }
  const hex = value.toString(16).toUpperCase().padStart(4, "0");
  return `${hex.slice(0, 2)}-${hex.slice(2)}`;
};

const empty = {
  name: "",
  businessId: "",
  serviceId: "",
  deviceDivision: "5e-04",
  enabled: false,
};

export default function SolaronPolicyPage() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(empty);
  const [show, setShow] = useState(false);

  const load = () =>
    apiWrapper(async () => {
      const result = await api.getPolicies();
      if (result.status) {
        setItems(result.data);
      }
    });

  useEffect(() => {
    load();
  }, []);

  const open = (item = null) => {
    setSelected(item);
    setForm(
      item
        ? {
            name: item.name,
            businessId: formatCode(item.businessId),
            serviceId: formatCode(item.serviceId),
            deviceDivision: item.deviceDivision,
            enabled: item.enabled,
          }
        : empty
    );
    setShow(true);
  };

  const change = (name, value) => {
    setForm({
      ...form,
      [name]: value,
    });
  };

  const save = async () => {
    if (!form.name.trim()) {
      return alert("정책명을 입력해주세요.");
    }

    if (parseCode(form.businessId) === null) {
      return alert("사업자 ID는 00-13과 같은 2바이트 16진수 형식으로 입력해주세요.");
    }

    if (parseCode(form.serviceId) === null) {
      return alert("서비스 ID는 00-13과 같은 2바이트 16진수 형식으로 입력해주세요.");
    }

    if (!/^[0-9A-Fa-f]{2}-[0-9A-Fa-f]{2}$/.test(form.deviceDivision)) {
      return alert("기기모델 코드는 예: 5e-04 형식으로 입력해주세요.");
    }

    const payload = {
      name: form.name.trim(),
      businessId: parseCode(form.businessId),
      serviceId: parseCode(form.serviceId),
      deviceDivision: form.deviceDivision.toLowerCase(),
      enabled: Boolean(form.enabled),
    };

    const result = selected
      ? await api.updatePolicy(selected.id, payload)
      : await api.createPolicy(payload);

    if (!result.status) {
      return alert(
        `저장에 실패했습니다.\n${result.data?.message || result.data || ""}`
      );
    }

    setShow(false);
    load();
  };

  const remove = async (item) => {
    if (
      !window.confirm(
        `"${item.name}" 정책을 삭제하시겠습니까?`
      )
    ) {
      return;
    }

    const result = await api.deletePolicy(item.id);

    if (!result.status) {
      return alert(
        `삭제에 실패했습니다.\n${result.data?.message || result.data || ""}`
      );
    }

    load();
  };

  const toggleEnabled = async (item) => {
    const payload = {
      name: item.name,
      businessId: item.businessId,
      serviceId: item.serviceId,
      deviceDivision: item.deviceDivision,
      enabled: !item.enabled,
    };

    const result = await api.updatePolicy(item.id, payload);

    if (!result.status) {
      return alert(
        `상태 변경에 실패했습니다.\n${result.data?.message || result.data || ""}`
      );
    }

    load();
  };

  return (
    <div className="print-area">
      <Container fluid className="mb-3 d-flex align-items-center">
        <h1 className="title">SOLARON 자동연동 정책</h1>
        <span className="ms-auto">
          <Button onClick={() => open()}>정책 추가</Button>
        </span>
      </Container>

      <Container fluid>
        <p className="text-muted">
          RTU 신규 등록 또는 수신한 사업자·서비스 ID 변경 시, 세 조건이 모두 일치하는
          활성 정책이 있으면 SOLARON 외부전송 API(ID 20)에 자동 등록됩니다.
          설정 전송 후 RTU가 변경된 ID로 통신하면 정책을 다시 검사합니다. 기존 외부 API 매핑은 유지됩니다.
        </p>

        <Table bordered hover>
          <thead className="table-header">
            <tr>
              <th>No</th>
              <th>정책명</th>
              <th>사업자 ID</th>
              <th>서비스 ID</th>
              <th>기기모델</th>
              <th>상태</th>
              <th>등록일</th>
              <th>관리</th>
            </tr>
          </thead>

          <tbody className="table-body">
            {items.map((item, index) => (
              <tr key={item.id} onDoubleClick={() => open(item)}>
                <td>{index + 1}</td>
                <td>{item.name}</td>
                <td>{formatCode(item.businessId) || String(item.businessId)}</td>
                <td>{formatCode(item.serviceId) || String(item.serviceId)}</td>
                <td>{item.deviceDivision}</td>
                <td>
                  <Badge bg={item.enabled ? "success" : "secondary"}>
                    {item.enabled ? "ON" : "OFF"}
                  </Badge>
                </td>
                <td>
                  {item.createdDate
                    ? new Date(item.createdDate).toLocaleString()
                    : "-"}
                </td>
                <td>
                  <Button
                    size="sm"
                    variant={item.enabled ? "secondary" : "success"}
                    onClick={() => toggleEnabled(item)}
                  >
                    {item.enabled ? "OFF" : "ON"}
                  </Button>
                  <Button
                    size="sm"
                    className="ms-2"
                    onClick={() => open(item)}
                  >
                    수정
                  </Button>
                  <Button
                    size="sm"
                    className="ms-2"
                    variant="danger"
                    onClick={() => remove(item)}
                  >
                    삭제
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Container>

      <Modal show={show} onHide={() => setShow(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            SOLARON 자동연동 정책 {selected ? "수정" : "추가"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={4}>
                정책명
              </Form.Label>
              <Col>
                <Form.Control
                  value={form.name}
                  onChange={(e) => change("name", e.target.value)}
                />
              </Col>
            </Form.Group>

            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={4}>
                사업자 ID
              </Form.Label>
              <Col>
                <Form.Control
                  type="text"
                  maxLength={5}
                  placeholder="예: 00-13"
                  value={form.businessId}
                  onChange={(e) => change("businessId", e.target.value.toUpperCase())}
                />
              </Col>
            </Form.Group>

            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={4}>
                서비스 ID
              </Form.Label>
              <Col>
                <Form.Control
                  type="text"
                  maxLength={5}
                  placeholder="예: 00-13"
                  value={form.serviceId}
                  onChange={(e) => change("serviceId", e.target.value.toUpperCase())}
                />
              </Col>
            </Form.Group>

            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={4}>
                기기모델
              </Form.Label>
              <Col>
                <Form.Control
                  maxLength={5}
                  placeholder="예: 5e-04"
                  value={form.deviceDivision}
                  onChange={(e) =>
                    change("deviceDivision", e.target.value.toLowerCase())
                  }
                />
              </Col>
            </Form.Group>

            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={4}>
                자동연동
              </Form.Label>
              <Col className="d-flex align-items-center">
                <Form.Check
                  type="switch"
                  checked={form.enabled}
                  onChange={(e) => change("enabled", e.target.checked)}
                  label={form.enabled ? "사용" : "미사용"}
                />
              </Col>
            </Form.Group>

            <Form.Text>
              신규 등록 또는 RTU가 변경된 사업자·서비스 ID로 통신할 때 적용됩니다.
            </Form.Text>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShow(false)}>
            취소
          </Button>
          <Button onClick={save}>저장</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
